/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useEffect } from 'react';

// redux
import { useSelector, useDispatch } from "react-redux";
import {
  fetchAccountRevenueShare,
  fetchSiteRevenueShare,
  updateAccountRevenueShare,
  updateSiteRevenueShare
} from "../../../../../state/thunks/settings";
import { updateUpdateStatus, resetToDB } from "../../../../../state/slices/settings";

// material
import ExpandableSection from "../../../../../material/expandablesection/expandablesection";
import ConfigController from "../../../../../material/expandablesection/controllers/configcontroller/configcontroller";
import Subscriptions from "./subscriptions";
import CustomRevShares from "./customRevShares";

// styles
import injectSheet from "react-jss";
import styles from './advanced_styles';

// static
const UPDATE_STATUS = 'advancedUpdateStatus'
const STATUS_IDLE = 'idle'
const STATUS_PENDING = 'pending'
const SUCCESS_MESSAGE = 'Update Success'
const ERROR_MESSAGE = 'Update Failed'

/* ========== ~~~~~~~~~~ ADVANCED SETTINGS ~~~~~~~~~~ ========== */
const Advanced = injectSheet(styles)((props) => {
  const dispatch = useDispatch();
  const {
    account: {
      sites
    },
    settings: {
      revenueShare,
      revenueShareFromDB,
      customRevenueShare,
      customRevenueShareFromDB,
      advancedUpdateStatus
    },
    session: {
      accountUUID
    }
  } = useSelector((state) => state);
  const [isOpened, setIsOpened] = useState(false);
  const [isChanged, setIsChanged] = useState(false);

  useEffect(() => {
    if (advancedUpdateStatus === 'success' || advancedUpdateStatus === 'error') setTimeout(() => {
      dispatch(updateUpdateStatus({ field: UPDATE_STATUS, status: STATUS_IDLE }))
    }, [2000])
  }, [advancedUpdateStatus])

  /*************** SAVE CHANGES VALIDATOR ***************/
  useEffect(() => {
    if (revenueShareFromDB) {
      setIsChanged(!revenueShare.every(isRevShareEqual))
    }
  }, [revenueShare])

  useEffect(() => {
    if (!customRevenueShareFromDB) return

    if (customRevenueShare.length !== customRevenueShareFromDB.length) {
      return setIsChanged(true)
    }
    setIsChanged(!customRevenueShare.every(isCustomRevShareEqual))
  }, [customRevenueShare])

  const isRevShareEqual = ((config, index) => {
    return Number(config.initialLoadRevenueSharePercentage).toFixed(2) === Number(revenueShareFromDB[index].initialLoadRevenueSharePercentage).toFixed(2)
      && Number(config.refreshRevenueSharePercentage).toFixed(2) === Number(revenueShareFromDB[index].refreshRevenueSharePercentage).toFixed(2)
  })

  const isCustomRevShareEqual = config => {
    const matchingRevShare = customRevenueShareFromDB.find(revShare => revShare.id === config.id)
    if (!matchingRevShare) return false
    return config.type === matchingRevShare.type
      && Number(config.initialLoadRevenueSharePercentage).toFixed(2) === Number(matchingRevShare.initialLoadRevenueSharePercentage).toFixed(2)
      && Number(config.refreshRevenueSharePercentage).toFixed(2) === Number(matchingRevShare.refreshRevenueSharePercentage).toFixed(2)
  }

  /*************** BUTTON HANDLERS ***************/
  const handleToggle = () => {
    dispatch(updateUpdateStatus({ field: UPDATE_STATUS, status: STATUS_IDLE }))
    setIsOpened(!isOpened);
  }

  const handleCancel = () => {
    setIsOpened(false)
    dispatch(resetToDB({ cache: 'revenueShare', db: 'revenueShareFromDB' }))
    dispatch(resetToDB({ cache: 'customRevenueShare', db: 'customRevenueShareFromDB' }))
  }

  const handleSave = async () => {
    setIsOpened(false)
    setIsChanged(false)
    dispatch(updateUpdateStatus({ field: UPDATE_STATUS, status: STATUS_PENDING }))

    const localeDate = new Date().toISOString().split('T')[0]

    await dispatch(updateAccountRevenueShare(getAccountRevShareRequestData()))
    await dispatch(updateSiteRevenueShare(getSiteRevShareRequestData()))
    await dispatch(fetchAccountRevenueShare({ localeDate, accountUUID }))
    await dispatch(fetchSiteRevenueShare({ localeDate, accountUUID }))
  }

  const getAccountRevShareRequestData = () => {
    return {
      accountUUID: accountUUID,
        revenueShare: revenueShare.map(revShare => ({
      ...revShare,
      initialLoadRevenueSharePercentage: revShare.initialLoadRevenueSharePercentage === ""
        ? 0.00
        : revShare.initialLoadRevenueSharePercentage / 100.0,
      refreshRevenueSharePercentage: revShare.refreshRevenueSharePercentage === ""
        ? 0.00
        : revShare.refreshRevenueSharePercentage / 100.0
    }))
    }
  }

  const getSiteRevShareRequestData = () => {
    return {
      accountUUID: accountUUID,
      revenueShare: customRevenueShare
        .filter(revShare => revShare.hasOwnProperty("type"))
        .map(revShare => ({
          ...revShare,
          initialLoadRevenueSharePercentage: revShare.initialLoadRevenueSharePercentage === ""
            ? 0.00
            : revShare.initialLoadRevenueSharePercentage / 100.0,
          refreshRevenueSharePercentage: revShare.refreshRevenueSharePercentage === ""
            ? 0.00
            : revShare.refreshRevenueSharePercentage / 100.0
        }))
    }
  }

  /*************** CONTROLLER & COMPONENT ***************/
  let statusMessage = "";
  switch (advancedUpdateStatus) {
    case "success":
      statusMessage = SUCCESS_MESSAGE
      break;
    case "failed":
      statusMessage = ERROR_MESSAGE
      break;
    case "idle":
      statusMessage = ""
      break;
  }
  const controller = (
    <ConfigController
      handleCancel={handleCancel}
      handleSave={handleSave}
      handleToggle={handleToggle}
      isOpened={isOpened}
      isChanged={isChanged}
      title='Advanced Settings'
      description={statusMessage}
      descriptionClass={advancedUpdateStatus === "success" ? props.classes.updateSuccess : props.classes.updateFailed}
    />
  );

  return (
    <ExpandableSection
      className={`invoice ${props.classes.advanced}`}
      controller={controller}
      isOpened={isOpened}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isChanged={isChanged}
    >
      {isOpened && <>
        <Subscriptions />
        {Object.keys(sites).length > 1 && <CustomRevShares />}
      </>}
    </ExpandableSection>
  )
});

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default Advanced;
